import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HateoasResourceService } from '@lagoshny/ngx-hateoas-client';
import { Resource as BaseResource } from '@lagoshny/ngx-hateoas-client';
import { lastValueFrom } from 'rxjs';
import { handle } from 'src/shared-utilities/async-helpers/promise-helpers';
import { ClientLanguageService } from 'src/shared-utilities/services/client-language.service';
import { ValidationGlobalStore } from '../../../../../utilities/validation/validation-global-store';
import { DashboardService } from '../../../dashboard/services/dashboard.service';
import { BorrowersValidatorStore } from '../../../slides/borrowers/validation/borrowers-validator.store';
import { GettingStartedValidatorStore } from '../../../slides/getting-started/validation/getting-started-validator.store';
import { IncomeValidationStore } from '../../../slides/income/validation/income-validation.store';
import { MenuService } from '../../../slides/menu/menu.service';
import { SubmitValidationStore } from '../../../slides/reviewAndSubmit/referral/validation/submit-validation.store';
import { AgentDetails } from '../../models/agent-resources/agent-resource';
import { Deal } from '../../models/deal-resources/deal.model';
import { ResourceCommandStream } from '../resource-command-stream.service';
import { ResourceContextStore } from '../resource-context-store';
import { ResourceTree, ResourceTreeCriteria } from '../resource-tree';
import { ClientJourneyEngagementService } from './client-journey-engagement.service';
import { APP_CONFIG, AppConfig } from 'src/environments/app-environment.models';

@Injectable({
  providedIn: 'root',
})
export class ClientJourneyContext {
  private readonly resourceService = inject(HateoasResourceService);
  private readonly appConfig = inject<AppConfig>(APP_CONFIG);
  private readonly http = inject(HttpClient);
  readonly engagementService = inject(ClientJourneyEngagementService);
  private readonly dashboardService = inject(DashboardService);
  readonly resourceCommandStream = inject(ResourceCommandStream);
  private readonly resourceContextStore = inject(ResourceContextStore);
  readonly menuService = inject(MenuService);
  private readonly clientLanguageService = inject(ClientLanguageService);

  private _agent: AgentDetails;
  get agent(): AgentDetails {
    return this._agent;
  }

  private _deal: Deal = null;
  get deal(): Deal {
    return this._deal;
  }

  private _validationGlobalStore: ValidationGlobalStore;
  get validationGlobalStore(): ValidationGlobalStore {
    if (!this._validationGlobalStore) {
      const globalStore = new ValidationGlobalStore();
      globalStore.addStore(
        new GettingStartedValidatorStore(globalStore, this),
        GettingStartedValidatorStore.globalStoreKey,
      );

      globalStore.addStore(new BorrowersValidatorStore(globalStore, this), BorrowersValidatorStore.globalStoreKey);

      globalStore.addStore(new IncomeValidationStore(globalStore, this), IncomeValidationStore.globalStoreKey);

      globalStore.addStore(new SubmitValidationStore(globalStore, this), SubmitValidationStore.globalStoreKey);

      this._validationGlobalStore = globalStore;
    }
    return this._validationGlobalStore;
  }

  private resourceTree: ResourceTree<Deal>;

  async init(agentDetails: AgentDetails) {
    this._agent = agentDetails;
    await this.resetDeal();
  }

  // Used when loading a new deal, initializing the agent,
  // or landing on the home page.
  async resetDeal() {
    await this.engagementService.stopTracking();
    this.resourceContextStore.reset();

    this._validationGlobalStore?.dispose();
    this._validationGlobalStore = null;

    this._deal = null;
  }

  private async buildResourceTree(shortCode: string, loanCode: string, dealKey: string): Promise<ResourceTree<Deal>> {
    const rootUrl = `${shortCode}/${dealKey}/${loanCode}`;
    const treeCriteria = new ResourceTreeCriteria(Deal, rootUrl)
      .include('$.organization', {
        params: { lang: this.clientLanguageService.clientLanguage },
      })
      .include('$.primaryMortgageRequest')
      .include('$.primaryMortgageRequest.mortgages', { isArray: true })
      .include('$.referral')
      .include('$.referral.contact')
      .include('$.note', { link: 'notes' })
      .include('$.subjectProperty')
      .include('$.subjectProperty.address', { link: 'addresses' })
      .include('$.borrowers', { isArray: true })
      .include('$.borrowers.employments', { isArray: true })
      .include('$.borrowers.employments.address')
      .include('$.borrowers.otherIncomes', {
        isArray: true,
        link: 'otherincomes',
      })
      .include('$.borrowers.bankScrapes', {
        isArray: true,
        link: 'bankscrapes',
      })
      .include('$.borrowers.properties', { isArray: true })
      .include('$.borrowers.properties.address')
      .include('$.borrowers.properties.downPayment', { link: 'downpayment' })
      .include('$.borrowers.properties.mortgages', { isArray: true })
      .include('$.borrowers.assets', { isArray: true })
      .include('$.borrowers.contact', { link: 'contacts' })
      .include('$.borrowers.contact.addresses', { isArray: true })
      .include('$.borrowers.cjEngagement')
      .include('$.borrowers.cjEngagement.pages', {
        isArray: true,
        link: 'cjEngagementPages',
      });

    this.resourceTree = new ResourceTree(treeCriteria, this.resourceService);
    return this.resourceTree;
  }

  public async loadDeal(shortCode: string, loanCode: string, dealKey: string): Promise<Deal> {
    await this.resetDeal();
    const resourceTree = await this.buildResourceTree(shortCode, loanCode, dealKey);
    const [_, trySaveError] = await handle(this.dashboardService.trySaveDeal(this._agent, loanCode, dealKey));
    if (trySaveError) {
      console.error(trySaveError);
    }

    this._deal = await resourceTree.load();

    await this.engagementService.startTracking(this);
    return this._deal;
  }

  // Example:
  // Path: '$.borrowers.contact' and contacts for all borrowers will be loaded
  async loadPath(root: Deal, path: string) {
    await this.resourceTree.loadPath(root, path);
  }

  // Example:
  // Path: '$.borrowers.contact' and parent borrowers (1...*) would load the contact
  // and contact child resources for those borrowers
  // Note parents are required.
  async loadPathForParents(path: string, parents: Array<BaseResource>) {
    await this.resourceTree.loadPathForParents(path, parents);
  }

  // Example:
  // Path: '$.borrowers.contact' will load all child data.
  // A resource is required.
  async loadResourceChildren(path: string, resources: Array<BaseResource>) {
    await this.resourceTree.loadResourceChildren(path, resources);
  }

  // Example:
  // Path: '$.borrowers.contact' returns an array of all contact objects (not grouped by borrower)
  getResources(root: Deal, path: string) {
    return this.resourceTree.getResources(root, path);
  }

  // Example:
  // Path: '$.borrowers.contact' returns an array of all borrower objects
  getParents(root: Deal, path: string) {
    return this.resourceTree.getParents(root, path);
  }

  public async createNewDeal(shortCode: string, ceContactKey?: string, customSource?: string): Promise<Deal> {
    const url = `${this.appConfig.publicApiBaseUrl}/v2/deal/${shortCode}`;
    const params: HttpParams = new HttpParams().set('ceContactKey', ceContactKey || '');

    const newDealRequest = {
      overrideDownPayment: true,
      customSource: customSource,
    };

    //Create new Deal
    const newDeal = await lastValueFrom(this.http.post<Deal>(url, newDealRequest, { params }));

    const deal = await this.loadDeal(this.agent.shortCode, newDeal.loanCode, newDeal.dealKey);

    return deal;
  }
}
